import type {
    Category,
    TransformedSubItemForMatrices,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/ui/___shared___/components/custom-category-selector/types.ts';

export interface TransformedSubItemForMatricesRanks extends TransformedSubItemForMatrices {
    itemOrder: number;
}

export const getNormalizedSubItems = (
    subItems: TransformedSubItemForMatricesRanks[],
    levelOneItems: Category[],
    levelTwoItems: Category[],
): TransformedSubItemForMatricesRanks[] => {
    const levelOneOrder = new Map(levelOneItems.map((it, idx) => [it.value, idx]));
    const levelTwoOrder = new Map(levelTwoItems.map((it, idx) => [it.value, idx]));

    return subItems
        .filter((subItem) => levelOneOrder.has(subItem.type) && levelTwoOrder.has(subItem.category))
        .sort((a, b) => {
            const levelOneOrderDiff = (levelOneOrder.get(a.type) ?? 0) - (levelOneOrder.get(b.type) ?? 0);
            if (levelOneOrderDiff !== 0) return levelOneOrderDiff;

            const labelCompare = a.label.localeCompare(b.label);
            if (labelCompare !== 0) return labelCompare;

            return (levelTwoOrder.get(a.category) ?? 0) - (levelTwoOrder.get(b.category) ?? 0);
        });
};

export const getNextSelectedGroupsAndTypes = (
    selectedGroupValues: string[],
    selectedTypeValues: string[],
    isLevelOneLabel: boolean,
    labelValue: string,
) => {
    const nextSelectedGroups = new Set(selectedGroupValues);
    const nextSelectedTypes = new Set(selectedTypeValues);

    if (isLevelOneLabel) {
        if (nextSelectedGroups.has(labelValue)) {
            nextSelectedGroups.delete(labelValue);
        } else {
            nextSelectedGroups.add(labelValue);
        }
    } else {
        if (nextSelectedTypes.has(labelValue)) {
            nextSelectedTypes.delete(labelValue);
        } else {
            nextSelectedTypes.add(labelValue);
        }
    }

    return {
        groups: [...nextSelectedGroups],
        types: [...nextSelectedTypes],
    };
};

export const getNextCheckedSubItems = (
    selectedGroupValues: string[],
    selectedTypeValues: string[],
    levelOneItems: Category[],
    levelTwoItems: Category[],
    allSubItems: TransformedSubItemForMatrices[],
) => {
    const activeGroups =
        selectedGroupValues.length > 0 ? new Set(selectedGroupValues) : new Set(levelOneItems.map((g) => g.value));

    const activeTypes =
        selectedTypeValues.length > 0 ? new Set(selectedTypeValues) : new Set(levelTwoItems.map((t) => t.value));

    return new Set(
        allSubItems
            .filter(({ type, category }) => activeGroups.has(type) && activeTypes.has(category))
            .map(({ value }) => String(value)),
    );
};

// TODO: Remove this function when backend returns normalized labels
const getNormalizedLevelTwoItemLabel = (label: string) => {
    if (label === 'Management') return 'Mgmt';
    if (label === 'Operational') return 'Ops';
    if (label === 'Support') return 'Sup';

    return label;
};

export const getNormalizedItems = (items: Category[], isLevelOne = true) =>
    items.map((item) => ({
        value: item.value,
        label: isLevelOne ? item.label : getNormalizedLevelTwoItemLabel(item.label),
    }));
